/**
 * Template Runtime for WYSIWYG Label Editor
 *
 * Re-exports the MOSS template runtime and provides evaluation utilities
 * for templates that use templateFn/labelFn fields.
 */

// Re-export everything from the MOSS runtime
export * from '@wysiwyg/moss-runtime';

// Import for internal use
import * as MossRuntime from '@wysiwyg/moss-runtime';
import { metrcGlobalsData } from '@wysiwyg/moss-runtime';
import type { MetrcGlobals } from '@wysiwyg/moss-runtime';
import { withResolvedTac } from '../utils/tac';
import type { Template } from './types';

// Initialize metrc globals with terpene/cannabinoid metadata on module load
MossRuntime.setMetrcGlobals(metrcGlobalsData);

// ============================================================================
// Context Types (wysiwyg-specific extensions)
// ============================================================================

export type EvalContext = {
  coa?: Record<string, any>;
  job?: Record<string, any>;
  theme?: Record<string, any>;
  metrc?: MetrcGlobals;
  product?: Record<string, any>;
  assets?: Record<string, any>;
};

export type LabelContext = EvalContext & {
  signetUrl?: string;
  signetUrl36?: string;
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Decode HTML/XML entities in a string
 */
const decodeHtmlEntities = (str: string): string => {
  if (typeof str !== 'string') return str;

  return str
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/&#(\d+);/g, (_, dec) => String.fromCharCode(parseInt(dec, 10)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));
};

/**
 * Recursively decode HTML entities in all string values of an object
 */
const decodeHtmlEntitiesInObject = (obj: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      result[key] = decodeHtmlEntities(value);
    } else if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      result[key] = decodeHtmlEntitiesInObject(value);
    } else {
      result[key] = value;
    }
  }
  return result;
};

/**
 * Flatten a nested object into dot-notation keys for variable substitution.
 * Arrays are flattened with numeric indices (e.g., terpene_list.0, terpene_list.1).
 */
export const flattenForVariables = (
  obj: Record<string, any>,
  prefix = '',
  result: Record<string, any> = {},
): Record<string, any> => {
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (Array.isArray(value)) {
      // Store the array itself for cases that need the whole array
      result[newKey] = value;
      // Also flatten each array element with numeric index
      for (let i = 0; i < value.length; i++) {
        const itemKey = `${newKey}.${i}`;
        const item = value[i];
        if (item !== null && typeof item === 'object' && !Array.isArray(item)) {
          flattenForVariables(item, itemKey, result);
        } else {
          result[itemKey] = item;
        }
      }
    } else if (value !== null && typeof value === 'object') {
      flattenForVariables(value, newKey, result);
    } else {
      result[newKey] = value;
    }
  }
  return result;
};

/**
 * Build theme data from template's themeVariables
 */
export const buildThemeFromVariables = (
  themeVariables?: Array<{ key: string; value: any }>,
): Record<string, any> => {
  if (!themeVariables || !Array.isArray(themeVariables)) return {};

  const theme: Record<string, any> = {};
  for (const variable of themeVariables) {
    const key = variable.key.replace(/^theme\./, '');
    theme[key] = variable.value;
  }
  return theme;
};

// ============================================================================
// Top Terpenes Computation
// ============================================================================

/**
 * Compute sorted top terpenes/cannabinoids as objects with label, value,
 * and a toString() that produces the legacy combined format.
 *
 * Each entry supports:
 *   entry.toString() → "β-Myrcene: 2.45%"   (backward-compatible)
 *   entry.label      → "β-Myrcene"
 *   entry.value      → "2.45%"
 */

export interface TopIngredientEntry {
  label: string;
  value: string;
  toString(): string;
}

const coercePercent = (value: unknown): number => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
};

const computeTopTerpenes = (
  terpenes: Record<string, { percent?: number | string }> | undefined,
): TopIngredientEntry[] => {
  if (!terpenes) return [];

  const excludeKeys = ['totalTerpenes', 'otherTerpenes', 'humulene'];
  const terpenesMeta = metrcGlobalsData.terpenes;

  return Object.entries(terpenes)
    .filter(([key]) => !excludeKeys.includes(key))
    .map(([key, value]) => ({
      key,
      percent: coercePercent(value?.percent),
      label: terpenesMeta[key]?.label ?? key,
    }))
    .filter((t) => t.percent > 0)
    .sort((a, b) => b.percent - a.percent)
    .map((t) => ({
      label: t.label,
      value: `${t.percent.toFixed(2)}%`,
      toString() {
        return `${this.label}: ${this.value}`;
      },
    }));
};

/**
 * Compute sorted top cannabinoids as objects with label, value,
 * and a toString() that produces the legacy combined format.
 * Sorted by percent descending, excludes totals and deprecated keys.
 */
export const computeTopCannabinoids = (
  ingredients: Record<string, { percent?: number | string }> | undefined,
): TopIngredientEntry[] => {
  if (!ingredients) return [];

  const excludeKeys = ['totalCannabinoids', 'totalActiveCannabinoids', 'totalThc', 'totalCbd', 'totalDelta9thc', 'thc', 'activeThc', 'activeCbd'];
  const cannabinoidsMeta = metrcGlobalsData.cannabinoids;

  return Object.entries(ingredients)
    .filter(([key]) => !excludeKeys.includes(key))
    .filter(([key]) => key in cannabinoidsMeta)
    .map(([key, value]) => ({
      key,
      percent: coercePercent(value?.percent),
      label: cannabinoidsMeta[key]?.label ?? key,
    }))
    .filter((c) => c.percent > 0)
    .sort((a, b) => b.percent - a.percent)
    .map((c) => ({
      label: c.label,
      value: `${c.percent.toFixed(2)}%`,
      toString() {
        return `${this.label}: ${this.value}`;
      },
    }));
};

// const mapLegacyVariables = (
//     templateElements: any[]
// ) => {
//     if (!templateElements?.length) return;
//
//     // Some legacy variable names do not imply whether they refer to a Weight or a Percentage,
//     // so we can map those names to the name of the variable which does include Weight (the default).
//     const mapLegacyToWeightVarKey = {
//         'coa.totals.cannabinoids': 'coa.ingredients.totalCannabinoids.weight.amount'
//     };
//
//     for (const element of templateElements) {
//         const legacyVarKey = element.variableKey;
//         const weightVarKey = mapLegacyToWeightVarKey[legacyVarKey];
//         if (weightVarKey?.length) {
//             // Use the weight key
//             element.variableKey = weightVarKey;
//
//             // Add the Weight suffix to the name (preceding the var key), for user's clarification
//             const variableFriendlyNameIndex = element.text?.indexOf(':');
//             if (variableFriendlyNameIndex >= 0) {
//                 const variableFriendlyName = element.text.substring(0, variableFriendlyNameIndex).trim();
//                 element.text = element.text.replace(variableFriendlyName, `${variableFriendlyName} Weight`);
//             }
//
//             // Replace the legacy key with the weight key, within the text
//             element.text = element.text?.replace(`{{${legacyVarKey}}}`, `{{${weightVarKey}}}`);
//         }
//     }
// };

// ============================================================================
// Runtime Function Injection
// ============================================================================

/**
 * Get all moss-runtime function names and values for injection into template scope
 */
const getMossRuntimeFunctions = (): { names: string[]; values: Function[] } => {
  const names: string[] = [];
  const values: Function[] = [];

  for (const key of Object.keys(MossRuntime)) {
    const value = (MossRuntime as Record<string, any>)[key];
    if (typeof value === 'function') {
      names.push(key);
      values.push(value);
    }
  }

  return { names, values };
};

// Cache since it doesn't change
const runtimeFunctions = getMossRuntimeFunctions();

// ============================================================================
// Template Evaluation
// ============================================================================

/**
 * Check if a template has a templateFn field for evaluation
 */
export const hasTemplateFn = (template: Record<string, any>): boolean => {
  return typeof template.templateFn === 'string' && template.templateFn.length > 0;
};

/**
 * Create a permissive scope proxy that returns undefined for missing variables
 * instead of throwing ReferenceError. This emulates MOSS's behavior where
 * `$undefinedVar?` gracefully returns undefined.
 */
const createPermissiveScope = (baseScope: Record<string, any>): Record<string, any> => {
  return new Proxy(baseScope, {
    has() {
      // Pretend we have every property so `with` statement catches all lookups
      return true;
    },
    get(target, prop) {
      if (prop === Symbol.unscopables) {
        return undefined;
      }
      if (prop in target) {
        return target[prop as string];
      }
      // Return undefined for missing variables instead of letting it throw
      return undefined;
    },
  });
};

/**
 * Evaluate a templateFn string with all moss-runtime helpers injected.
 * Uses a permissive scope to handle undefined variables gracefully,
 * matching MOSS's behavior where `$undefinedVar?` returns undefined.
 */
export const evaluateTemplateFn = (
  templateFnString: string,
  context: EvalContext,
): Record<string, any> => {
  const { coa = {}, job = {}, theme = {}, product = {}, assets = {} } = context;
  const metrc = MossRuntime.getMetrcGlobals();

  try {
    // Build a scope object with all available variables and functions
    const scope: Record<string, any> = {
      coa,
      job,
      theme,
      metrc,
      product,
      assets,
    };

    // Add all runtime functions to scope
    for (let i = 0; i < runtimeFunctions.names.length; i++) {
      scope[runtimeFunctions.names[i]] = runtimeFunctions.values[i];
    }

    // Create permissive scope that returns undefined for missing variables
    const permissiveScope = createPermissiveScope(scope);

    // Use `with` statement to make all scope properties available as bare variables
    // and the Proxy ensures undefined variables return undefined instead of throwing
    const wrappedFn = new Function(
      'scope',
      `with (scope) {
        ${templateFnString}
        return evaluate(coa, job, theme, metrc, product, assets);
      }`,
    );

    const result = wrappedFn(permissiveScope);
    return result ? decodeHtmlEntitiesInObject(result) : {};
  } catch (err) {
    console.error('Failed to evaluate templateFn:', err);
    return {};
  }
};

/**
 * Evaluate a labelFn string for per-label context (e.g., signet URLs).
 * Uses a permissive scope to handle undefined variables gracefully.
 */
export const evaluateLabelFn = (
  labelFnString: string,
  templateContext: Record<string, any>,
  labelContext: LabelContext,
): Record<string, any> => {
  const { coa = {}, job = {}, theme = {}, signetUrl = '', signetUrl36 = '' } = labelContext;
  const metrc = MossRuntime.getMetrcGlobals();

  try {
    // Build a scope object with all available variables and functions
    const scope: Record<string, any> = {
      ctx: templateContext,
      coa,
      job,
      theme,
      metrc,
      signetUrl,
      signetUrl36,
    };

    // Add all runtime functions to scope
    for (let i = 0; i < runtimeFunctions.names.length; i++) {
      scope[runtimeFunctions.names[i]] = runtimeFunctions.values[i];
    }

    // Create permissive scope that returns undefined for missing variables
    const permissiveScope = createPermissiveScope(scope);

    const wrappedFn = new Function(
      'scope',
      `with (scope) {
        ${labelFnString}
        return evaluateLabel(ctx, coa, job, theme, metrc, signetUrl, signetUrl36);
      }`,
    );

    const result = wrappedFn(permissiveScope);
    return result || {};
  } catch (err) {
    console.error('Failed to evaluate labelFn:', err);
    return {};
  }
};

/**
 * Evaluate template and return data for variable substitution.
 * The data is kept in its natural nested structure - valueForKeyPath handles dot-notation traversal.
 */
export const evaluateTemplate = (
  template: Record<string, any>,
  context: EvalContext,
): Record<string, any> => {
  if (!hasTemplateFn(template)) {
    return {};
  }

  const theme = {
    ...buildThemeFromVariables(template.themeVariables),
    ...context.theme,
  };

  const templateResult = evaluateTemplateFn(template.templateFn, {
    ...context,
    theme,
  });

  return templateResult;
};

// ============================================================================
// Render Data Preparation
// ============================================================================

/**
 * Input options for preparing render data.
 * This consolidates all data sources needed for template rendering.
 */
export type PrepareRenderDataOptions = {
  /** The template being rendered */
  template: Partial<Pick<Template, 'elements' | 'tacAdjustments' | 'labelFn'>> & {
    //elements: string[];
    themeVariables?: Array<{ key: string; value: any; type?: string }>;
    templateFn?: string;
  };
  /** COA/lab test data (nested structure) */
  coa?: Record<string, any>;
  /** Job data */
  job?: Record<string, any>;
  /** Product data */
  product?: Record<string, any>;
  /** Common/general data (package info, org info, etc.) */
  common?: Record<string, any>;
  /** Additional assets data */
  assets?: Record<string, any>;
};

/**
 * Output from preparing render data.
 * Contains all computed data needed for template rendering.
 */
export type PreparedRenderData = {
  /**
   * Fully merged data for variable substitution.
   * Contains: common + coa + product + job + themeData + templateFnData
   */
  fullData: Record<string, any>;
  /** Theme data built from template.themeVariables */
  themeData: Record<string, any>;
  /** Data computed by evaluating template.templateFn (if present) */
  templateFnData: Record<string, any>;
  /**
   * Raw data context for second-pass variable resolution.
   * Used for resolving prefixed variables like {{theme.x}}, {{coa.x}}, {{topTerpenes.x}}, {{topCannabinoids.x}}, etc.
   */
  rawDataContext: {
    tacReportedCoa?: Record<string, unknown>;
    tacReportedFunctionData?: Record<string, unknown>;
    coa?: Record<string, any>;
    job?: Record<string, any>;
    product?: Record<string, any>;
    theme?: Record<string, any>;
    metrc?: Record<string, any>;
    topTerpenes?: TopIngredientEntry[];
    topCannabinoids?: TopIngredientEntry[];
  };
};

/**
 * Prepare all data needed for template rendering.
 *
 * This is the single source of truth for data preparation, used by both:
 * - LabelEditor (WYSIWYG preview)
 * - pdfGenerator (PDF export)
 * - StartPrintDialog (Print preview)
 *
 * The function:
 * 1. Builds theme data from template.themeVariables
 * 2. Evaluates template.templateFn (if present) to generate computed data
 * 3. Merges all data sources in the correct order
 * 4. Creates rawDataContext for prefixed variable resolution
 *
 * @example
 * ```typescript
 * const { fullData, themeData, rawDataContext } = prepareRenderData({
 *   template,
 *   coa: coaData,
 *   job: jobData,
 *   common: { packageTag: '1A4...', printDate: '2024-01-01' },
 * });
 *
 * // Use fullData for variable substitution
 * const text = renderTextWithData('{{productName}}', fullData);
 *
 * // Use rawDataContext for prefixed variables
 * const text2 = renderTextWithData('{{coa.batch}}', fullData, undefined, rawDataContext);
 * ```
 */
export const prepareRenderData = (options: PrepareRenderDataOptions): PreparedRenderData => {
  const { template, job = {}, product = {}, common = {}, assets = {} } = options;
  const coa = withResolvedTac(options.coa ?? {}, template);
  const tacReportedCoa = withResolvedTac(options.coa ?? {}, { ...template, elements: template.elements?.map(element =>
    element.type === 'text' ? { ...element, tacCalculationEnabled: false } : element) });

  //prep data with aliases
  const normalizedProduct: Record<string, any> = product
    ? {
        ...product,
        title: product.title ?? product.name,
        strainName: product.strainName ?? product.strain,
        subSpecies: product.subSpecies ?? product.subspeciesName,
        upc: product.upc ?? product.inventoryUpc,
      }
    : {};

  // Step 1: Build theme data from template.themeVariables
  const themeData = buildThemeFromVariables(template.themeVariables);

  // Step 2: Compute top terpenes array (sorted by percent descending)
  const topTerpenes = computeTopTerpenes(coa?.terpenes);

  // Step 2b: Compute top cannabinoids array (sorted by percent descending)
  const topCannabinoids = computeTopCannabinoids(coa?.ingredients);

  // Step 2c: Map legacy/unit-less variables to corresponding Weight variables.
  // mapLegacyVariables(template.elements);

  // Step 3: Evaluate templateFn if present
  // This generates computed data from COA/job/product inputs (e.g., formatted terpene lists)
  let templateFnData: Record<string, any> = {};
  if (hasTemplateFn(template)) {
    templateFnData = evaluateTemplate(template, {
      coa,
      job,
      product: normalizedProduct,
      theme: themeData,
      metrc: MossRuntime.getMetrcGlobals(),
      assets,
    });
  }
  let tacReportedFunctionData: Record<string, unknown> | undefined;
  if (coa.ingredients?.totalActiveCannabinoids?.percent !== tacReportedCoa.ingredients?.totalActiveCannabinoids?.percent &&
    template.elements?.some(element => element.type === 'text' && element.tacCalculationEnabled === true) &&
    template.elements.some(element => element.type === 'text' && element.tacCalculationEnabled !== true)) {
    const reportedResult = evaluateTemplate(template, {
      coa: tacReportedCoa, job, product: normalizedProduct, theme: themeData,
      metrc: MossRuntime.getMetrcGlobals(), assets,
    });
    const reportedBase: Record<string, unknown> = { ...common, ...tacReportedCoa, ...normalizedProduct, ...job, ...themeData };
    // Missing reported outputs must not retain aliases from the calculated run.
    tacReportedFunctionData = {
      ...Object.fromEntries(Object.keys(templateFnData).map(key => [key, reportedBase[key]])),
      ...reportedResult,
    };
  }

  // Step 4: Merge all data sources
  // Order matters: later sources override earlier ones
  // templateFnData comes last so computed values take precedence
  const fullData: Record<string, any> = {
    ...common,
    ...coa,
    ...normalizedProduct,
    ...job,
    ...themeData,
    ...templateFnData,
    topTerpenes,
    topCannabinoids,
  };

  // Step 5: Build raw data context for prefixed variable resolution
  // This enables {{coa.x}}, {{job.x}}, {{theme.x}}, {{topTerpenes.x}}, {{topCannabinoids.x}} style variables
  const rawDataContext = {
    ...fullData,
    coa,
    tacReportedCoa,
    tacReportedFunctionData,
    job,
    product: normalizedProduct,
    theme: themeData,
    metrc: MossRuntime.getMetrcGlobals(),
    topTerpenes,
    topCannabinoids,
  };

  return {
    fullData,
    themeData,
    templateFnData,
    rawDataContext,
  };
};
